import { installTawkTelemetryGuard } from "./tawk-telemetry-guard";

declare global {
  interface Window {
    Tawk_API?: any;
    Tawk_LoadStart?: Date;
  }
}

/** Pending DOM cleanup from removeTawkTo — cancelled if we re-init quickly. */
let removeCleanupTimer: ReturnType<typeof setTimeout> | null = null;

type PathRule =
  | { match: "exact"; path: string }
  | { match: "prefix"; path: string };

function normalizePath(pathname: string): string {
  const path = (pathname || "/").split("?")[0] || "/";
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

function matchesRule(path: string, rule: PathRule): boolean {
  if (rule.match === "exact") return path === rule.path;
  return path === rule.path || path.startsWith(`${rule.path}/`);
}

/**
 * Purchase / account / agent / recharge / activation flow steps — Tawk must never load here.
 * Checked first so these always win over the enable list.
 *
 * Keep this list in sync with application routes in `src/app/index.tsx`.
 */
const TAWK_DISABLED_ROUTES: PathRule[] = [
  { match: "prefix", path: "/quick-recharge" },
  { match: "prefix", path: "/checkout" },
  { match: "prefix", path: "/network-plan" },
  { match: "prefix", path: "/digital-security-selection" },
  { match: "prefix", path: "/single-network-selection" },
  { match: "prefix", path: "/single-network-phone-type" },
  { match: "prefix", path: "/single-network-number-type" },
  { match: "prefix", path: "/multi-network-selection" },
  { match: "prefix", path: "/multi-network-phone-type" },
  { match: "prefix", path: "/multi-network-number-type" },
  { match: "prefix", path: "/multi-network-number-information" },
  { match: "prefix", path: "/my-account" },
  { match: "prefix", path: "/agent" },
  { match: "prefix", path: "/activation/fixed-phone" },
  { match: "prefix", path: "/activation/preloaded-sim" },
];

/**
 * Landing, marketing, support, legal, and customer onboarding (/login, /activation).
 * Anything not listed here (and not already disabled) defaults to off.
 *
 * Keep this list in sync with customer-facing routes in `src/app/index.tsx`.
 */
const TAWK_ENABLED_ROUTES: PathRule[] = [
  { match: "exact", path: "/" },
  // Customer onboarding
  { match: "exact", path: "/login" },
  { match: "exact", path: "/forgot-password" },
  // Activation landing only
  { match: "exact", path: "/activation" },
  // Marketing / product
  { match: "prefix", path: "/platform-drivers" },
  { match: "prefix", path: "/pricing" },
  { match: "prefix", path: "/how-to-switch" },
  { match: "prefix", path: "/about-us" },
  { match: "prefix", path: "/how-vle-mobile-works" },
  { match: "prefix", path: "/why-vle-mobile" },
  { match: "prefix", path: "/fixed-phone" },
  { match: "prefix", path: "/family-plans" },
  { match: "prefix", path: "/broadband-facts" },
  { match: "exact", path: "/digital-security" },
  { match: "prefix", path: "/sim-swap-protection" },
  { match: "prefix", path: "/network-coverage" },
  { match: "prefix", path: "/single-network-coverage" },
  { match: "prefix", path: "/multi-network-coverage" },
  { match: "prefix", path: "/international-calling" },
  { match: "prefix", path: "/imei-check" },
  { match: "prefix", path: "/about-esim" },
  { match: "prefix", path: "/schedule-call" },
  // Support / content
  { match: "prefix", path: "/faq" },
  { match: "prefix", path: "/resource-center" },
  { match: "prefix", path: "/blog" },
  { match: "prefix", path: "/blog-detail" },
  { match: "prefix", path: "/support-center" },
  { match: "prefix", path: "/support-tickets" },
  // Legal
  { match: "prefix", path: "/e911" },
  { match: "prefix", path: "/network-management-policy" },
  { match: "prefix", path: "/accessibility" },
  { match: "prefix", path: "/return-policy" },
  { match: "prefix", path: "/terms-and-conditions" },
  { match: "prefix", path: "/privacy-policy" },
  { match: "prefix", path: "/deletion-suspension-policy" },
  { match: "prefix", path: "/do-not-sell" },
];

/** True when the current route should mount the Tawk.to widget. */
export function shouldEnableTawk(pathname: string): boolean {
  const path = normalizePath(pathname);

  if (TAWK_DISABLED_ROUTES.some((rule) => matchesRule(path, rule))) {
    return false;
  }

  return TAWK_ENABLED_ROUTES.some((rule) => matchesRule(path, rule));
}

if (typeof window !== "undefined") {
  installTawkTelemetryGuard();
}

export const initializeTawkTo = () => {
  if (typeof window === "undefined") return;

  installTawkTelemetryGuard();

  // Cancel any pending remove cleanup so it cannot wipe a fresh init
  if (removeCleanupTimer) {
    clearTimeout(removeCleanupTimer);
    removeCleanupTimer = null;
  }

  // Prevent multiple injections
  if (document.getElementById("tawk-script")) return;

  // If the script was removed (e.g., from a restricted page), but memory objects remain,
  // we need to wipe them out now. Otherwise, the re-injected script thinks the widget
  // is already fully initialized and refuses to rebuild the iframe and DOM elements.
  try {
    delete (window as any).Tawk_Window;
    delete (window as any).$_Tawk;
  } catch (e) {}

  // Re-initialize for fresh boot
  window.Tawk_API = {};
  window.Tawk_LoadStart = new Date();

  const s1 = document.createElement("script");
  // Use specific ID to track this script
  s1.id = "tawk-script";
  s1.async = true;
  s1.src = "https://embed.tawk.to/685c287eeef0f3190ce9b749/1iujvsb7e";
  s1.charset = "UTF-8";
  s1.setAttribute("crossorigin", "*");

  const s0 = document.getElementsByTagName("script")[0];
  if (s0 && s0.parentNode) {
    s0.parentNode.insertBefore(s1, s0);
  } else {
    document.head.appendChild(s1);
  }
};

export const openTawkToChat = () => {
  if (typeof window === "undefined") return;

  // Only open/init when the current route is allowed to host Tawk
  if (!shouldEnableTawk(window.location.pathname)) return;

  if (window.Tawk_API && window.Tawk_API.maximize) {
    window.Tawk_API.maximize();
  } else {
    initializeTawkTo();
  }
};

export const removeTawkTo = () => {
  if (typeof window === "undefined") return;

  if (removeCleanupTimer) {
    clearTimeout(removeCleanupTimer);
    removeCleanupTimer = null;
  }

  // Utilize Tawk's built-in methods first if available to stop sound/status
  if (window.Tawk_API) {
    try {
      if (window.Tawk_API.hideWidget) window.Tawk_API.hideWidget();
    } catch (e) {}
  }

  // Remove the main injected script
  document
    .querySelectorAll('script[src*="tawk.to"]')
    .forEach((s) => s.remove());
  const script = document.getElementById("tawk-script");
  if (script) script.remove();

  // Remove exact style elements provided in the DOM reference
  document
    .querySelectorAll("style.tawk-global-variables")
    .forEach((style) => style.remove());

  // Remove any iframes loaded from tawk.to or containing the chat widget
  document.querySelectorAll("iframe").forEach((iframe) => {
    try {
      const title = iframe.title.toLowerCase();
      if (
        iframe.src.includes("tawk.to") ||
        title.includes("chat widget") ||
        title.includes("chat") ||
        iframe.style.zIndex === "1000003" ||
        iframe.style.zIndex === "2147483647"
      ) {
        // Clearing src stops network requests in progress inside the iframe (including Websocket audio)
        iframe.src = "about:blank";
        iframe.remove();
      }
    } catch (e) {}
  });

  // Remove DOM containers injected by Tawk
  const tawkDivs = document.querySelectorAll(
    'div[class*="tawk"], div[id*="tawk"]'
  );
  tawkDivs.forEach((div) => div.remove());

  // Look for the specific Tawk widget wrapper at body level (deferred; cancelled on re-init)
  removeCleanupTimer = setTimeout(() => {
    removeCleanupTimer = null;
    const customWrappers = document.querySelectorAll(
      'div[style*="z-index: 2147483647"], div[style*="z-index: 1000003"]'
    );
    customWrappers.forEach((div) => {
      // Typically the Tawk widget has this extremely high z-index and uses an iframe inside
      if (
        div.innerHTML.includes("tawk.to") ||
        div.innerHTML.includes("chat") ||
        div.innerHTML.includes("tawk")
      ) {
        div.remove();
      }
    });

    // Reset window.Tawk_API cleanly for next initialization
    // We explicitly avoid deleting Tawk_Window or $_Tawk because Tawk's
    // lingering setTimeouts/setIntervals crash if these disappear unexpectedly.
    window.Tawk_API = {};
  }, 100);
};
